import React, { useState, useEffect } from 'react';
import { CdsContentPanel, CdsForm, CdsInputField } from 'clayco-component-library/lib';
import { Col, Row } from 'react-bootstrap';
import Spinner from "../../skills-experience/shared/spinner";
import * as yup from 'yup';

const Names = ({ data, apiProvider }) => {
    const [isDirty, setIsDirty] = useState(false);
    const [loading, setLoading] = useState(true);
    const [model, setModel] = useState({});
    const [error, setError] = useState(null);

    const schema = yup.object().shape({
        legalFirstName: yup.string().required('Legal First Name is required'),
        legalLastName: yup.string().required('Legal Last Name is required'),
        preferredName: yup.string(),
        presentationName: yup.string()
    });

    async function loadNameInfo() {
        setLoading(true);
        setError(null);
        try {
            const result = await apiProvider.Get("Profile/GetNameInfo");
            if (!result.error) {
                setModel(result);
                setError(null);
            } else {
                throw new Error('Invalid response');
            }
        } catch (err) {
            console.error("Name info fetch failed:", err);
            setError("Failed to load name information. Please try again later.");
        }
        setLoading(false);
    }

    useEffect(() => {
        if (data) {
            setModel(data);
            setLoading(false);
        } else {
            loadNameInfo();
        }
    }, []);

    const onFieldChange = (value, field) => {
        let update = { ...model };
        if (update.record) {
            update.record = { ...update.record };
        }
        switch (field) {
            case 'legalFirstName':
                update.record.legalFirstName = value;
                break;
            case 'legalLastName':
                update.record.legalLastName = value;
                break;
            case 'preferredName':
                update.record.preferredName = value;
                break;
            case 'presentationName':
                update.record.presentationName = value;
                break;
            default:
                return;
        }
        setModel(update);
        setIsDirty(true);
    };

    const onSave = async (frmData) => {
        setLoading(true);
        try {
            const castedData = schema.cast(frmData);
            const originalData = data.record;
            const changedFields = {};

    // Compare only the fields in original data
    for (const key in castedData) {
      if (castedData[key] !== originalData[key]) {
        changedFields[key] = castedData[key];
      }
    }
        console.log("Changed Fields: ", changedFields);
            await apiProvider.Patch("Profile/UpdateNameInfo", changedFields);
            setIsDirty(false);
            await loadNameInfo(); // reload fresh data
        } catch (err) {
            console.error("Save failed:", err);
            setError("Failed to save changes. Please try again.");
        }
        setLoading(false);
    };

    const handleError = (error) => {
        console.error("Validation error:", error);
    };

    const buildLabel = (label, pending) => (
        pending ? (
            <>
                <span>{label} {'\u00A0'}</span>
                <span style={{ color: 'var(--bs-purple)' }}>
                    Pending Change: {pending}
                </span>
            </>
        ) : (
            <span>{label}</span>
        )
    );

    return (
        <div className='personalInfo-names'>
            <CdsForm schema={schema} onSubmit={onSave} onError={handleError}>
                <CdsContentPanel
                    title='Names'
                    primaryButtonText='Save'
                    primaryButtonSubmits={true}
                    disablePrimaryButton={!isDirty || loading || !!error || !!model?.pending}
                >

                    {loading ? (
                        <Spinner show={true} />
                    ) : 
                    
                    error ? (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '150px',
                            textAlign: 'center',
                            color: '#B00020',
                            backgroundColor: '#FFF4F4',
                            borderRadius: '4px',
                            padding: '1rem',
                            fontWeight: '500'
                        }}>
                            {error}
                        </div>
                    ) : (
                        <>
                            {model?.pending && (
                                <div style={{
                                    backgroundColor: 'rgb(234, 170, 0)',
                                    color: 'black',
                                    padding: '10px',
                                    border: '1px solid rgb(234, 170, 0)',
                                    borderRadius: '4px',
                                    marginBottom: '10px',
                                }}>
                                    Your changes are pending approval.
                                </div>
                            )}
                            <Row>
                                <Col md={6}>
                                    <CdsInputField
                                        initialValue={model?.record?.legalFirstName}
                                        label={buildLabel('Legal First Name', model?.pending?.legalFirstName)}
                                        required={true}
                                        name='legalFirstName'
                                        onChange={(value) => onFieldChange(value, 'legalFirstName')}
                                        allowMultiple={false}
                                    />
                                </Col>
                                <Col md={6}>
                                    <CdsInputField
                                        initialValue={model?.record?.legalLastName}
                                        label={buildLabel('Legal Last Name', model?.pending?.legalLastName)}
                                        required={true}
                                        name='legalLastName'
                                        onChange={(value) => onFieldChange(value, 'legalLastName')}
                                        allowMultiple={false}
                                    />
                                </Col>
                            </Row>

                            <br />
                            <Row>
                                <Col md={6}>
                                    <CdsInputField
                                        initialValue={model?.record?.preferredName}
                                        label={buildLabel('Preferred First Name (Example: Mike in lieu of Michael)', model?.pending?.preferredName)}
                                        name='preferredName'
                                        onChange={(value) => onFieldChange(value, 'preferredName')}
                                        allowMultiple={false}
                                    />
                                </Col>
                            </Row>

                            <br />
                            <Row>
                                <Col md={6}>
                                    <CdsInputField
                                        initialValue={model?.record?.presentationName}
                                        label={buildLabel('Presentation Name (Email Signature)', model?.pending?.presentationName)}
                                        name='presentationName'
                                        onChange={(value) => onFieldChange(value, 'presentationName')}
                                        allowMultiple={false}
                                    />
                                </Col>
                            </Row>
                        </>
                    )}
                </CdsContentPanel>
            </CdsForm>
        </div>
    );
};

export default Names;